import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { makeStyles } from "@material-ui/core";
import { Select } from "antd";
import { createPortal } from "react-dom";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_FONT,
  PL_TH_BG,
  PL_TH_TEXT,
  PL_TH_BORDER,
} from "../pl-by-program/plByProgramTheme";
import { formatActualsThroughDate } from "../pl-by-program/plTimeFilterUtils";
import {
  buildWaterfallEacApiFilters,
  buildWaterfallEacDownloadFilters,
  getApiFilterKey,
  aggregateEacVarianceRows,
} from "./waterfallEacFilterUtils";
import { hasAnyTableFilters } from "../tableTotalUtils";
import { useReportFonts } from "../../reportFonts";
import {
  getEACVarianceViewLevel2API,
  getVersionsAPI,
  downloadEACVarianceViewLevel2API,
} from "../../../../api/reports-api";
import Loading from "../../../../common-components/Loading";
import {
  calcFilterPos,
  calcOpDropdownPos,
  resolveDefaultBudgetVersion,
} from "./filterPopoverUtils";
import {
  NumericFilterContent,
  OpDropdownContent,
} from "./tableColumnFilterShared";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
  fmtPctDisplay,
  cellWhiteSpaceStyle,
  dynamicColSize,
} from "./waterfallTableUtils";

import {
  TH_BG,
  TH_BG_BAND,
  CELL_FG,
  CELL_FG_STRONG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  ACCENT_COLOR,
  Z_HEAD,
  Z_STICKY,
} from "../../reportTableConfig";

/* ─── Design tokens ─────────────────────────────────────────────── */
const NEG = "#D64550";
const POS_VAR = "#166534";
const TOTAL_FG = CELL_FG_STRONG;
const ACCENT = ACCENT_COLOR;
const ACCENT2 = "#EEF2FF";

/* ─── Operator config ───────────────────────────────────────────── */
const OP_LABELS = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=", neq: "≠" };
const OP_ENTRIES = [
  { key: "gt", label: "> Greater than" },
  { key: "gte", label: "≥ Greater than or equal" },
  { key: "lt", label: "< Less than" },
  { key: "lte", label: "≤ Less than or equal" },
  { key: "eq", label: "= Equals" },
  { key: "neq", label: "≠ Not equal" },
];

function applyNumFilter(v, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const n = Number(v ?? 0),
    fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":
      return n > fv;
    case "gte":
      return n >= fv;
    case "lt":
      return n < fv;
    case "lte":
      return n <= fv;
    case "eq":
      return Math.abs(n - fv) < 0.001;
    case "neq":
      return Math.abs(n - fv) >= 0.001;
    default:
      return true;
  }
}

/* ─── Fixed columns (Level 2 — CLIN grain) ─────────────────────── */
const FIXED_DEFS = [
  { key: "projectId", label: "Project #", w: 102, align: "center" },
  { key: "contractName", label: "Contract Name", w: 200, align: "left" },
  { key: "pm", label: "Program Manager", w: 172, align: "left" },
  { key: "popStart", label: "POP Start Date", w: 130, align: "center" },
  { key: "popEnd", label: "POP End Date", w: 120, align: "center" },
  { key: "contractType", label: "Contract Type", w: 128, align: "center" },
];
let _fixLeft = 0;
FIXED_DEFS.forEach((c) => {
  c.left = _fixLeft;
  _fixLeft += c.w;
});

/* ─── Measures (8 — same for all three groups) ──────────────────── */
const MEASURES = [
  { key: "contractRevenue", label: "Revenue", w: 160, isPct: false },
  { key: "directLabor", label: "Direct Labor", w: 130, isPct: false },
  { key: "subcontractor", label: "Subcontractor", w: 136, isPct: false },
  { key: "odcs", label: "ODCs", w: 100, isPct: false },
  { key: "grossMargin", label: "Gross Margin", w: 136, isPct: false },
  { key: "grossMarginPct", label: "GM%", w: 78, isPct: true },
  { key: "netProfit", label: "Net Profit / Fee", w: 162, isPct: false },
  { key: "netMarginPct", label: "NM%", w: 78, isPct: true },
];

const GROUP_KEYS = ["liveEac", "variance"];

/* ─── Layout constants ──────────────────────────────────────────── */
const GH = 32;
const CH = 34;

/* ─── Data helpers ──────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

function buildBlock(rawBlock) {
  const out = {};
  for (const m of MEASURES) {
    const v = rawBlock?.[m.key] ?? 0;
    out[m.key] = m.isPct ? (v != null ? v * 100 : null) : (v ?? null);
  }
  return out;
}

function buildVariance(live, sel) {
  const out = {};
  for (const m of MEASURES) {
    out[m.key] = (live[m.key] ?? 0) - (sel[m.key] ?? 0);
  }
  return out;
}

function transformProject(project) {
  const live = buildBlock(project.liveEac);
  const sel = buildBlock(project.selected);
  return {
    projectId: project.id,
    projectName: project.projectName || "",
    contractName: project.contractName || "",
    customerName: project.customerName || "",
    bu: project.bu || "",
    pm: project.programManager || "",
    popStart: fmtDate(project.popStartDate),
    popEnd: fmtDate(project.popEndDate),
    contractType: project.contractType || "",
    tcvCeiling: project.ceiling,
    tcvFunded: project.funded,
    liveEac: live,
    selected: sel,
    variance: buildVariance(live, sel),
  };
}

function transformGrandTotals(gt) {
  const live = buildBlock(gt?.liveEac);
  const sel = buildBlock(gt?.selected);
  return {
    projectId: "",
    projectName: "",
    contractName: "Total",
    customerName: "",
    bu: "",
    pm: "",
    popStart: "",
    popEnd: "",
    contractType: "",
    tcvCeiling: null,
    tcvFunded: null,
    isTotal: true,
    liveEac: live,
    selected: sel,
    variance: buildVariance(live, sel),
  };
}

/* ─── Formatters ────────────────────────────────────────────────── */
const fmtMoney2 = (v) => {
  if (v == null || v === "") return "";
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `($${abs})` : `$${abs}`;
};
const fmtCell = (v, isPct) => (isPct ? fmtPctDisplay(v) : fmtMoney2(v));

/* ─── Cell color ────────────────────────────────────────────────── */
function cellColor(v, gKey, isTotal) {
  if (v == null) return isTotal ? TOTAL_FG : CELL_FG;
  if (gKey === "variance") {
    if (v > 0.001) return POS_VAR;
    if (v < -0.001) return NEG;
    return isTotal ? TOTAL_FG : CELL_FG;
  }
  if (v < 0) return NEG;
  return isTotal ? TOTAL_FG : CELL_FG;
}

/* ─── Month names ───────────────────────────────────────────────── */
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ─── Group stripe backgrounds ──────────────────────────────────── */
const GROUP_BG_EVEN = ["#FFFFFF", "#FAFAF8", "#EFF6FF"];
const GROUP_BG_ODD = ["#F7FAFC", "#F2F1EE", "#E6F0FB"];
const GROUP_TOTAL = ["#F0FFF4", "#F5F5F0", "#E8F2FF"];

/* ─── Sticky header helpers ─────────────────────────────────────── */
const thCore = (top, h, z, padding, bg) => ({
  position: "sticky",
  top,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: bg,
  color: PL_TH_TEXT,
  verticalAlign: "middle",
  padding,
  whiteSpace: "nowrap",
  border: "none",
  overflow: "visible",
  boxShadow: `inset -1px 0 0 ${PL_TH_BORDER}, inset 0 -1px 0 ${PL_TH_BORDER}`,
});
const thFixedGroup = (fixedW) => ({
  ...thCore(0, GH, Z_HEAD + 1, "10px 16px", PL_TH_BG),
  left: 0,
  ...dynamicColSize(fixedW),
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  textAlign: "left",
});
const thFixedCol = (align, col) => ({
  ...thCore(GH, CH, Z_HEAD + 1, "10px 14px", PL_TH_BG),
  left: col.left,
  ...dynamicColSize(col.w),
  ...(col.isNum
    ? {}
    : { whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }),
  fontSize: 11,
  fontWeight: 500,
  textAlign: align,
});
const thScrollGroup = () => ({
  ...thCore(0, GH, Z_HEAD, "10px 10px", PL_TH_BG),
  fontSize: 10,
  fontWeight: 700,
  textAlign: "center",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
});
const thScrollCol = (align) => ({
  ...thCore(GH, CH, Z_HEAD, "10px 8px", PL_TH_BG),
  fontSize: 11,
  fontWeight: 500,
  textAlign: align,
});

/* ─── Data row helpers ──────────────────────────────────────────── */
const tdBase = (bg, align, fw = 400, isNumeric = true) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: align,
  fontWeight: fw,
  padding: "7px 10px",
  fontSize: 12,
  color: CELL_FG,
  ...cellWhiteSpaceStyle(isNumeric),
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});
const tdFixed = (bg, align, col, fw, isLast = false) => ({
  ...tdBase(bg, align, fw, !!col.isNum),
  position: "sticky",
  left: col.left,
  zIndex: Z_STICKY,
  ...dynamicColSize(col.w),
  color: CELL_FG,
  boxShadow: isLast ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
});

/* ─── Icon components ───────────────────────────────────────────── */
function ColsIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="18" rx="1" />
      <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
  );
}
function FilterFunnelIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/* ─── Styles ────────────────────────────────────────────────────── */
const useStyles = makeStyles(() => ({
  tableSection: {
    marginBottom: 24,
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "18px 24px 14px",
    borderBottom: "1px solid #E2E8F0",
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    flexShrink: 0,
    background: "#FFFFFF",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.02em",
    fontFamily: PL_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: "#6B7280",
    fontFamily: PL_FONT,
  },
  tableScrollPane: { overflowX: "auto" },
  tableBody: {
    overflow: "hidden",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  table: {
    borderCollapse: "collapse",
    borderSpacing: 0,
    tableLayout: "fixed",
    fontFamily: PL_FONT,
    fontSize: 11,
  },

  sectionHeadRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  sectionHeadRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },

  versionPicker: {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
    flexShrink: 0,
  },
  versionPickerLabel: {
    padding: "0 10px",
    height: 34,
    display: "flex",
    alignItems: "center",
    background: "rgba(99,102,241,0.08)",
    borderRight: "1px solid #E2E8F0",
    fontSize: 11,
    fontWeight: 700,
    color: "#3730A3",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    whiteSpace: "nowrap",
    fontFamily: PL_FONT,
    userSelect: "none",
  },

  colPickerWrap: { position: "relative", flexShrink: 0 },
  colPickerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 34,
    padding: "0 14px",
    borderRadius: 8,
    border: "1px solid #E2E8F0",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: PL_FONT,
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#CBD5E1" },
  },
  colHiddenBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4338CA",
    background: "rgba(99,102,241,0.12)",
    borderRadius: 4,
    padding: "1px 6px",
    marginLeft: 2,
  },
  colPickerDropdown: {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    zIndex: 1200,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    padding: "6px 0 8px",
    minWidth: 220,
    maxHeight: 420,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  colPickerHeader: {
    fontSize: 10,
    fontWeight: 700,
    color: "#9CA3AF",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    padding: "6px 16px 8px",
    fontFamily: PL_FONT,
    borderBottom: "1px solid #F3F4F6",
    marginBottom: 4,
  },
  colPickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 16px",
    cursor: "pointer",
    fontSize: 13,
    color: "#374151",
    fontFamily: PL_FONT,
    userSelect: "none",
    "&:hover": { background: "#F8FAFC" },
    "& input[type='checkbox']": {
      cursor: "pointer",
      width: 14,
      height: 14,
      accentColor: "#4338CA",
      flexShrink: 0,
    },
  },
  colPickerReset: {
    margin: "6px 12px 0",
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #E2E8F0",
    background: "transparent",
    color: "#6B7280",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: PL_FONT,
    textAlign: "center",
    "&:hover": { background: "#F3F4F6", color: "#374151" },
  },

  filterIconBtn: {
    flexShrink: 0,
    width: 16,
    height: 16,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    borderRadius: 3,
    color: PL_TH_TEXT,
    opacity: 0.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { opacity: 1, background: "rgba(255,255,255,0.14)" },
  },
  filterIconBtnActive: {
    opacity: 1,
    color: "#A5B4FC",
    background: "rgba(165,180,252,0.18)",
    borderRadius: 3,
  },

  filterPanel: {
    position: "fixed",
    zIndex: 99,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "10px 12px",
    minWidth: 180,
    fontFamily: PL_FONT,
  },
  filterTextInput: {
    flex: 1,
    height: 24,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 6px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#9CA3AF" },
  },
  filterNumInput: {
    width: "100%",
    height: 24,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 4px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    textAlign: "right",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#CBD5E1" },
  },
  filterOpBtn: {
    flexShrink: 0,
    width: 20,
    height: 24,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    background: "#fff",
    fontSize: 11,
    fontWeight: 700,
    color: ACCENT,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { background: ACCENT2, borderColor: ACCENT },
  },
  filterOpBtnActive: {
    background: ACCENT,
    borderColor: ACCENT,
    color: "#fff",
    "&:hover": { background: "#4338CA" },
  },
  filterClearBtn: {
    flexShrink: 0,
    width: 16,
    height: 16,
    border: "none",
    borderRadius: "50%",
    background: "#E2E8F0",
    fontSize: 9,
    color: "#64748B",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { background: "#CBD5E1" },
  },
  filterWrap: { display: "flex", alignItems: "center", gap: 3, height: 24 },

  opDropdown: {
    position: "fixed",
    zIndex: 101,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 190,
    overflow: "hidden",
    fontFamily: PL_FONT,
  },
  opDropdownItem: {
    display: "block",
    width: "100%",
    padding: "8px 14px",
    fontSize: 12,
    color: "#374151",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: PL_FONT,
    "&:hover": { background: ACCENT2, color: ACCENT },
  },
  opDropdownItemActive: { background: ACCENT2, color: ACCENT, fontWeight: 600 },

  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 20px",
    borderTop: "1px solid #E2E8F0",
    background: "#FAFAFA",
    flexShrink: 0,
    gap: 12,
    flexWrap: "wrap",
  },
  paginationInfo: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: PL_FONT,
    whiteSpace: "nowrap",
  },
  paginationRight: { display: "flex", alignItems: "center", gap: 8 },
  paginationControls: { display: "flex", alignItems: "center", gap: 3 },
  pageSizeLabel: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: PL_FONT,
    whiteSpace: "nowrap",
  },
  pageSizeSelect: {
    height: 28,
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    fontSize: 12,
    color: "#374151",
    padding: "0 6px",
    background: "#fff",
    cursor: "pointer",
    outline: "none",
    fontFamily: PL_FONT,
    "&:focus": { borderColor: ACCENT },
  },
  pageBtn: {
    minWidth: 30,
    height: 28,
    padding: "0 6px",
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    background: "#fff",
    fontSize: 12,
    color: TH_BG_BAND,
    cursor: "pointer",
    fontFamily: PL_FONT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    "&:hover:not(:disabled)": {
      background: "#F1F5F9",
      borderColor: "#94A3B8",
      color: TH_BG,
    },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  },
  pageBtnActive: {
    background: TH_BG_BAND,
    borderColor: TH_BG_BAND,
    color: "#fff",
    fontWeight: 600,
    "&:hover": { background: TH_BG_BAND },
  },
  pageDots: {
    fontSize: 12,
    color: "#6B7280",
    padding: "0 4px",
    userSelect: "none",
  },

  errorBox: {
    padding: "48px 24px",
    textAlign: "center",
    fontFamily: PL_FONT,
    color: "#D64550",
    fontSize: 13,
  },
  stateBox: {
    padding: "48px 24px",
    textAlign: "center",
    fontFamily: PL_FONT,
    color: "#6B7280",
    fontSize: 13,
  },
}));

/* ─── Main component ────────────────────────────────────────────── */
export default function EACVarianceViewLevel2({
  filters = {},
  downloadRef,
  setParentDownloading,
}) {
  const plClasses = usePlByProgramStyles();
  const localClasses = useStyles();
  const classes = { ...plClasses, ...localClasses };
  useReportFonts();

  /* ── Version dropdown (required) ── */
  const [version, setVersion] = useState("");
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);

  /* ── API data ── */
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ── Column visibility ── */
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  /* ── Table filters ── */
  const [searchProjectId, setSearchProjectId] = useState("");
  const [searchContractName, setSearchContractName] = useState("");
  const [searchPm, setSearchPm] = useState("");
  const [searchPopStart, setSearchPopStart] = useState("");
  const [searchPopEnd, setSearchPopEnd] = useState("");
  const [searchContractType, setSearchContractType] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [openOpKey, setOpenOpKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const opBtnRefs = useRef({});

  /* ── Filter panel ── */
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  /* ── Pagination ── */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ── Sorting — array of {key, dir} for multi-level sort ── */
  const [sortKeys, setSortKeys] = useState([]);

  useEffect(() => {
    if (!pickerOpen) return;
    const h = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target))
        setPickerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [pickerOpen]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]")
      )
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPanelPos(calcFilterPos(btn));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setDropdownPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    repositionPanels();
  }, [repositionPanels]);

  /* Keep filter popovers anchored below header buttons during page/table scroll */
  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);

  /* ── Fetch versions ── */
  useEffect(() => {
    setVersionsLoading(true);
    setVersion("");
    getVersionsAPI(filters.year || "")
      .then((res) => {
        const opts = (res?.data || []).filter((v) => v.key !== "liveEac");
        setVersions(opts);
        if (opts.length > 0) setVersion(resolveDefaultBudgetVersion(res?.data));
      })
      .finally(() => setVersionsLoading(false));
  }, [filters.year]);

  /* ── Fetch data ── */
  const apiFilterKey = useMemo(
    () => getApiFilterKey(filters, buildWaterfallEacApiFilters),
    [filters],
  );

  const handleDownload = async () => {
    setParentDownloading?.(true);
    try {
      const { blob, filename } = await downloadEACVarianceViewLevel2API(
        buildWaterfallEacDownloadFilters(filters),
        version,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      /* silent */
    } finally {
      setParentDownloading?.(false);
    }
  };
  if (downloadRef) downloadRef.current = handleDownload;

  useEffect(() => {
    if (!version) return;
    setLoading(true);
    setError(null);
    getEACVarianceViewLevel2API(buildWaterfallEacApiFilters(filters), version)
      .then((res) => {
        if (res?.status === 200 && res?.data) setApiData(res.data);
        else {
          setError(res?.message || "Failed to load data");
          setApiData(null);
        }
      })
      .catch(() => {
        setError("Failed to load data");
        setApiData(null);
      })
      .finally(() => setLoading(false));
  }, [apiFilterKey, version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
  }, [
    searchProjectId,
    searchContractName,
    searchPm,
    searchPopStart,
    searchPopEnd,
    searchContractType,
    colFilters,
    filters,
    version,
  ]);

  /* ── Derived rows ── */
  const allRows = useMemo(() => {
    if (!apiData) return [];
    return [
      transformGrandTotals(apiData.grandTotals),
      ...(apiData.projects || []).map(transformProject),
    ];
  }, [apiData]);

  const totalRow = allRows[0] || null;
  const projectRows = allRows.slice(1);

  /* ── Visible fixed columns ── */
  const visibleFixed = useMemo(() => {
    let left = 0;
    return FIXED_DEFS.filter((c) => !hiddenCols.has(c.key)).map((c) => {
      const col = { ...c, left };
      left += c.w;
      return col;
    });
  }, [hiddenCols]);

  const visibleMeasures = MEASURES.filter((m) => !hiddenCols.has(m.key));
  const fixedW = visibleFixed.reduce((s, c) => s + c.w, 0);
  const groupW = visibleMeasures.reduce((s, m) => s + m.w, 0);
  const tableW = fixedW + GROUP_KEYS.length * groupW;

  const versionOptions = useMemo(
    () => (versions || []).map((v) => ({ value: v.key, label: v.name })),
    [versions],
  );

  const groupLabels = useMemo(
    () => ({
      liveEac: apiData?.live?.name || "Live EAC",
      selected: apiData?.selected?.name || "EAC Version",
      variance: "Variance",
    }),
    [apiData],
  );

  const liveActualsThrough =
    apiData?.actualThroughMonth != null
      ? MONTH_NAMES[apiData.actualThroughMonth - 1]
      : null;
  const actualsThroughDate = formatActualsThroughDate(
    apiData?.actualThroughMonth,
  );
  const groupHeaderH = liveActualsThrough ? 44 : GH;

  const updateColFilter = (key, field, value) =>
    setColFilters((prev) => ({
      ...prev,
      [key]: { op: "gt", ...prev[key], [field]: value },
    }));
  const clearColFilter = (key) =>
    setColFilters((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  const isNumActive = (key) => {
    const f = colFilters[key];
    return f && f.value !== "" && f.value !== undefined;
  };

  const toggleOpDropdown = (key) => {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };
  const selectOp = (key, op) => {
    updateColFilter(key, "op", op);
    setOpenOpKey(null);
  };

  const getTextFilterEntry = (key) => {
    const map = {
      projectId: [searchProjectId, setSearchProjectId, "Search ID…"],
      contractName: [searchContractName, setSearchContractName, "Search name…"],
      pm: [searchPm, setSearchPm, "Search PM…"],
      popStart: [searchPopStart, setSearchPopStart, "Start date…"],
      popEnd: [searchPopEnd, setSearchPopEnd, "End date…"],
      contractType: [searchContractType, setSearchContractType, "Type…"],
    };
    return map[key] || null;
  };

  const hasFilterValue = (key) => {
    const tf = getTextFilterEntry(key);
    return tf ? !!tf[0] : isNumActive(key);
  };

  const renderFilterContent = (key) => {
    const tf = getTextFilterEntry(key);
    if (tf) return renderTextFilter(tf[0], tf[1], tf[2]);
    return renderNumFilter(key);
  };

  const toggleFilter = (key) => {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  const rowPasses = (row) => {
    if (
      searchProjectId &&
      !String(row.projectId || "")
        .toLowerCase()
        .includes(searchProjectId.toLowerCase())
    )
      return false;
    if (
      searchContractName &&
      !String(row.contractName || "")
        .toLowerCase()
        .includes(searchContractName.toLowerCase())
    )
      return false;
    if (
      searchPm &&
      !String(row.pm || "")
        .toLowerCase()
        .includes(searchPm.toLowerCase())
    )
      return false;
    if (
      searchContractType &&
      !String(row.contractType || "")
        .toLowerCase()
        .includes(searchContractType.toLowerCase())
    )
      return false;
    if (
      searchPopStart &&
      !String(row.popStart || "")
        .toLowerCase()
        .includes(searchPopStart.toLowerCase())
    )
      return false;
    if (
      searchPopEnd &&
      !String(row.popEnd || "")
        .toLowerCase()
        .includes(searchPopEnd.toLowerCase())
    )
      return false;
    for (const gk of GROUP_KEYS) {
      for (const m of MEASURES) {
        const fk = `${gk}_${m.key}`;
        if (
          isNumActive(fk) &&
          !applyNumFilter((row[gk] || {})[m.key], colFilters[fk])
        )
          return false;
      }
    }
    return true;
  };

  /* ── Sorting ── */
  const handleSort = (colKey) =>
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  const onThClick = createSortThClick(handleSort);

  const getSortValue = (row, colKey) => {
    if (FIXED_DEFS.some((c) => c.key === colKey)) return row[colKey];
    const us = colKey.indexOf("_");
    if (us > 0) {
      const gk = colKey.slice(0, us);
      const mk = colKey.slice(us + 1);
      if (GROUP_KEYS.includes(gk)) return (row[gk] || {})[mk];
    }
    return row[colKey];
  };

  const getSortColumnLabel = (colKey) => {
    const fixed = FIXED_DEFS.find((c) => c.key === colKey);
    if (fixed) return fixed.label;
    const us = colKey.indexOf("_");
    if (us > 0) {
      const gk = colKey.slice(0, us);
      const mk = colKey.slice(us + 1);
      if (GROUP_KEYS.includes(gk)) {
        const m = MEASURES.find((meas) => meas.key === mk);
        return m ? `${groupLabels[gk]} ${m.label}` : colKey;
      }
    }
    return colKey;
  };

  /* ── Pagination ── */
  const filteredRows = projectRows.filter(rowPasses);
  const visibleRows =
    sortKeys.length === 0
      ? filteredRows
      : [...filteredRows].sort((a, b) =>
          compareBySortKeys(a, b, sortKeys, getSortValue),
        );
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = visibleRows.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );
  const rangeStart =
    visibleRows.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, visibleRows.length);
  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  const hasRowFilters = useMemo(
    () =>
      hasAnyTableFilters(
        searchProjectId,
        searchContractName,
        searchPm,
        searchContractType,
        searchPopStart,
        searchPopEnd,
        GROUP_KEYS.some((gk) =>
          MEASURES.some((m) => isNumActive(`${gk}_${m.key}`)),
        ),
      ),
    [
      searchProjectId,
      searchContractName,
      searchPm,
      searchContractType,
      searchPopStart,
      searchPopEnd,
      colFilters,
    ],
  );

  const displayTotalRow = useMemo(() => {
    if (!totalRow) return null;
    if (!hasRowFilters) return totalRow;
    return aggregateEacVarianceRows(filteredRows, MEASURES) ?? totalRow;
  }, [totalRow, hasRowFilters, filteredRows]);

  const projectCount = allRows.length > 0 ? allRows.length - 1 : 0;
  const toggleCol = (key) =>
    setHiddenCols((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const renderTextFilter = (value, onChange, placeholder) => (
    <div className={classes.filterWrap}>
      <input
        type="text"
        className={classes.filterTextInput}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button className={classes.filterClearBtn} onClick={() => onChange("")}>
          ✕
        </button>
      )}
    </div>
  );

  const renderNumFilter = (colKey) => {
    const f = colFilters[colKey] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={getSortColumnLabel(colKey)}
        value={f.value ?? ""}
        op={f.op || "gt"}
        onValueChange={(v) => updateColFilter(colKey, "value", v)}
        onClear={() => clearColFilter(colKey)}
        onToggleOp={() => toggleOpDropdown(colKey)}
        opBtnRef={(el) => {
          opBtnRefs.current[colKey] = el;
        }}
      />
    );
  };

  const isLoading = versionsLoading || (!!version && loading);
  const showNoVersion = !versionsLoading && !version;

  return (
    <div className={plClasses.root}>
      <div className={plClasses.container}>
        <PlPageHeader
          classes={classes}
          eyebrow="Waterfall / EAC"
          title="Waterfall / EAC — EAC Variance View Level 2"
          accent="Level 2"
          meta={
            isLoading
              ? "Loading…"
              : `${projectCount} CLINs · Live EAC Vs. ${groupLabels.selected}`
          }
          metaTop={
            apiData?.fiscalYear
              ? `FY-${apiData.fiscalYear}`
              : filters.year
                ? `FY-${filters.year}`
                : ""
          }
          date={actualsThroughDate ?? undefined}
        />

        <div className={classes.tableSection}>
          <div className={classes.tableSectionHead}>
            <div className={classes.sectionHeadRow}>
              <div>
                <h2 className={classes.tableSectionTitle}>
                  EAC Variance Analysis — Level 2
                </h2>
                <div className={classes.tableSectionMeta}>
                  {`Live EAC Vs. ${groupLabels.selected} · ${visibleRows.length} of ${projectCount} CLINs`}
                  {sortKeys.length > 0 && (
                    <span
                      style={{ marginLeft: 8, color: ACCENT, fontWeight: 500 }}
                    >
                      · sorted by{" "}
                      {sortKeys
                        .map((s) => getSortColumnLabel(s.key))
                        .join(", ")}
                    </span>
                  )}
                </div>
              </div>

              <div className={classes.sectionHeadRight}>
                <div className={classes.versionPicker}>
                  <span className={classes.versionPickerLabel}>Vs.</span>
                  <Select
                    size="small"
                    bordered={false}
                    style={{ minWidth: 130 }}
                    value={version || undefined}
                    placeholder={
                      versionsLoading ? "Loading…" : "Select version"
                    }
                    onChange={setVersion}
                    options={versionOptions}
                    popupMatchSelectWidth={false}
                    loading={versionsLoading}
                  />
                </div>

                <div ref={pickerRef} className={classes.colPickerWrap}>
                  <button
                    type="button"
                    className={classes.colPickerBtn}
                    onClick={() => setPickerOpen((p) => !p)}
                  >
                    <ColsIcon />
                    Columns
                    {hiddenCols.size > 0 && (
                      <span className={classes.colHiddenBadge}>
                        {hiddenCols.size} hidden
                      </span>
                    )}
                  </button>
                  {pickerOpen && (
                    <div className={classes.colPickerDropdown}>
                      <div className={classes.colPickerHeader}>
                        Fixed Columns
                      </div>
                      {FIXED_DEFS.map((c) => (
                        <label key={c.key} className={classes.colPickerItem}>
                          <input
                            type="checkbox"
                            checked={!hiddenCols.has(c.key)}
                            onChange={() => toggleCol(c.key)}
                          />
                          {c.label}
                        </label>
                      ))}
                      <div
                        className={classes.colPickerHeader}
                        style={{ marginTop: 4 }}
                      >
                        Measures (all groups)
                      </div>
                      {MEASURES.map((m) => (
                        <label key={m.key} className={classes.colPickerItem}>
                          <input
                            type="checkbox"
                            checked={!hiddenCols.has(m.key)}
                            onChange={() => toggleCol(m.key)}
                          />
                          {m.label}
                        </label>
                      ))}
                      {hiddenCols.size > 0 && (
                        <button
                          type="button"
                          className={classes.colPickerReset}
                          onClick={() => setHiddenCols(new Set())}
                        >
                          Show All Columns
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isLoading && <Loading />}
          {!isLoading && showNoVersion && (
            <div className={classes.stateBox}>
              Select a version to compare against Live EAC.
            </div>
          )}
          {!isLoading && !showNoVersion && error && (
            <div className={classes.errorBox}>{error}</div>
          )}
          {!isLoading && !showNoVersion && !error && allRows.length === 0 && (
            <div className={classes.stateBox}>
              No data for the selected filters.
            </div>
          )}

          {!isLoading && !showNoVersion && !error && allRows.length > 0 && (
            <div className={classes.tableBody}>
              <div
                className={classes.tableScrollPane}
                onScroll={handleTableScroll}
              >
                <table
                  className={classes.table}
                  style={{ width: tableW, minWidth: tableW }}
                >
                  <colgroup>
                    {visibleFixed.map((c) => (
                      <col key={c.key} style={dynamicColSize(c.w)} />
                    ))}
                    {GROUP_KEYS.map((gk) =>
                      visibleMeasures.map((m) => (
                        <col
                          key={`${gk}_${m.key}`}
                          style={dynamicColSize(m.w)}
                        />
                      )),
                    )}
                  </colgroup>

                  <thead>
                    <tr style={{ height: groupHeaderH }}>
                      <th
                        colSpan={visibleFixed.length}
                        style={{
                          ...thFixedGroup(fixedW),
                          height: groupHeaderH,
                        }}
                        aria-hidden
                      />
                      {GROUP_KEYS.map(
                        (gk) =>
                          visibleMeasures.length > 0 && (
                            <th
                              key={gk}
                              colSpan={visibleMeasures.length}
                              style={{
                                ...thScrollGroup(),
                                height: groupHeaderH,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <span>{groupLabels[gk]}</span>
                                {gk === "liveEac" && liveActualsThrough && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 400,
                                      opacity: 0.75,
                                      textTransform: "none",
                                      letterSpacing: "normal",
                                    }}
                                  >
                                    Actuals through {liveActualsThrough}
                                  </span>
                                )}
                              </div>
                            </th>
                          ),
                      )}
                    </tr>
                    <tr>
                      {visibleFixed.map((c) => {
                        const fActive =
                          openFilterKey === c.key || hasFilterValue(c.key);
                        return (
                          <th
                            key={c.key}
                            style={{
                              ...thFixedCol(c.align, c),
                              cursor: "pointer",
                            }}
                            onClick={onThClick(c.key)}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  c.align === "right"
                                    ? "flex-end"
                                    : "space-between",
                                gap: 4,
                                minWidth: 0,
                              }}
                            >
                              <WaterfallThLabel
                                label={c.label}
                                colKey={c.key}
                                sortKeys={sortKeys}
                                getLabel={getSortColumnLabel}
                                highlight={openFilterKey === c.key}
                                align={c.align}
                              />
                              <button
                                ref={(el) => {
                                  filterBtnRefs.current[c.key] = el;
                                }}
                                data-filter-btn
                                className={`${classes.filterIconBtn} ${fActive ? classes.filterIconBtnActive : ""}`}
                                onClick={() => toggleFilter(c.key)}
                                title="Filter"
                              >
                                <FilterFunnelIcon />
                              </button>
                            </div>
                          </th>
                        );
                      })}
                      {GROUP_KEYS.map((gk) =>
                        visibleMeasures.map((m) => {
                          const colKey = `${gk}_${m.key}`;
                          const fActive =
                            openFilterKey === colKey || hasFilterValue(colKey);
                          return (
                            <th
                              key={colKey}
                              style={{
                                ...thScrollCol("right"),
                                ...dynamicColSize(m.w),
                                cursor: "pointer",
                              }}
                              onClick={onThClick(colKey)}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  gap: 4,
                                  minWidth: 0,
                                }}
                              >
                                <WaterfallThLabel
                                  label={m.label}
                                  colKey={colKey}
                                  sortKeys={sortKeys}
                                  getLabel={getSortColumnLabel}
                                  highlight={openFilterKey === colKey}
                                  align="right"
                                />
                                <button
                                  ref={(el) => {
                                    filterBtnRefs.current[colKey] = el;
                                  }}
                                  data-filter-btn
                                  className={`${classes.filterIconBtn} ${fActive ? classes.filterIconBtnActive : ""}`}
                                  onClick={() => toggleFilter(colKey)}
                                  title="Filter"
                                >
                                  <FilterFunnelIcon />
                                </button>
                              </div>
                            </th>
                          );
                        }),
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {displayTotalRow &&
                      (() => {
                        const fw = 700;
                        return (
                          <tr key="total">
                            {visibleFixed.map((c, ci) => {
                              const display = displayTotalRow[c.key] ?? "";
                              return (
                                <td
                                  key={c.key}
                                  style={{
                                    ...tdFixed(
                                      TOTAL_BG,
                                      c.align,
                                      c,
                                      fw,
                                      ci === visibleFixed.length - 1,
                                    ),
                                    color: TOTAL_FG,
                                  }}
                                  title={
                                    typeof display === "string" ? display : ""
                                  }
                                >
                                  {display}
                                </td>
                              );
                            })}
                            {GROUP_KEYS.map((gk, gi) =>
                              visibleMeasures.map((m) => {
                                const v = (displayTotalRow[gk] || {})[m.key];
                                const tbg =
                                  gk === "variance"
                                    ? GROUP_TOTAL[2] || TOTAL_BG
                                    : GROUP_TOTAL[gi] || TOTAL_BG;
                                return (
                                  <td
                                    key={`${gk}_${m.key}`}
                                    style={{
                                      ...tdBase(tbg, "right", fw),
                                      color: cellColor(v, gk, true),
                                      ...dynamicColSize(m.w),
                                    }}
                                  >
                                    {fmtCell(v, m.isPct)}
                                  </td>
                                );
                              }),
                            )}
                          </tr>
                        );
                      })()}

                    {pagedRows.map((row, ri) => {
                      const globalIdx = (safePage - 1) * pageSize + ri;
                      const baseBg = globalIdx % 2 === 0 ? EVEN_BG : ODD_BG;
                      return (
                        <tr key={row.projectId || `r-${ri}`}>
                          {visibleFixed.map((c, ci) => {
                            const display = row[c.key] ?? "";
                            return (
                              <td
                                key={c.key}
                                style={{
                                  ...tdFixed(
                                    baseBg,
                                    c.align,
                                    c,
                                    400,
                                    ci === visibleFixed.length - 1,
                                  ),
                                  color: CELL_FG,
                                }}
                                title={
                                  typeof display === "string" ? display : ""
                                }
                              >
                                {display}
                              </td>
                            );
                          })}
                          {GROUP_KEYS.map((gk, gi) =>
                            visibleMeasures.map((m) => {
                              const v = (row[gk] || {})[m.key];
                              const rbg =
                                gi === 0
                                  ? baseBg
                                  : globalIdx % 2 === 0
                                    ? GROUP_BG_EVEN[2]
                                    : GROUP_BG_ODD[2];
                              return (
                                <td
                                  key={`${gk}_${m.key}`}
                                  style={{
                                    ...tdBase(rbg, "right", 400),
                                    color: cellColor(v, gk, false),
                                    ...dynamicColSize(m.w),
                                  }}
                                >
                                  {fmtCell(v, m.isPct)}
                                </td>
                              );
                            }),
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className={classes.paginationBar}>
                <span className={classes.paginationInfo}>
                  {visibleRows.length === 0
                    ? "No CLINs match filters"
                    : `${rangeStart}–${rangeEnd} of ${visibleRows.length} CLINs`}
                </span>
                <div className={classes.paginationRight}>
                  <span className={classes.pageSizeLabel}>Rows Per Page</span>
                  <select
                    className={classes.pageSizeSelect}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <div className={classes.paginationControls}>
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === 1}
                      onClick={() => setPage(1)}
                      title="First"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="7,2 3,6 7,10" />
                        <polyline points="11,2 7,6 11,10" />
                      </svg>
                    </button>
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === 1}
                      onClick={() => setPage((p) => p - 1)}
                      title="Previous"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="8,2 4,6 8,10" />
                      </svg>
                    </button>
                    {pageNumbers.map((p, i) => (
                      <React.Fragment key={p}>
                        {i > 0 && pageNumbers[i - 1] < p - 1 && (
                          <span className={classes.pageDots}>…</span>
                        )}
                        <button
                          className={`${classes.pageBtn} ${p === safePage ? classes.pageBtnActive : ""}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      title="Next"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="4,2 8,6 4,10" />
                      </svg>
                    </button>
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === totalPages}
                      onClick={() => setPage(totalPages)}
                      title="Last"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="5,2 9,6 5,10" />
                        <polyline points="1,2 5,6 1,10" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            className={classes.filterPanel}
            style={{ top: filterPanelPos.top, left: filterPanelPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={colFilters[openOpKey]?.op || "gt"}
            onSelect={(op) => selectOp(openOpKey, op)}
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}
